import React, { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles, mergeClasses, tokens, Button } from '@fluentui/react-components';
import { iconMap } from '../../theme/icons';

const CloseIcon = iconMap.close;
const ChevronLeftIcon = iconMap.chevron_left;
const ChevronRightIcon = iconMap.chevron_right;

// Pilno ekrano fono pritemdymas (85% juoda) yra sąmoningas dekoratyvus
// sprendimas paveikslėlio kontrastui — NE Fluent overlay token'as
// (colorBackgroundOverlay ~40% juoda, per šviesu čia), tad lieka literali
// reikšmė. Tas pats pasakytina apie permatomus baltus mygtukus ant jos.
const useStyles = makeStyles({
  overlay: {
    position: 'fixed', inset: 0, zIndex: tokens.zIndexOverlay,
    backgroundColor: 'rgba(0,0,0,0.85)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: tokens.spacingHorizontalL,
    '@media (min-width: 640px)': { padding: tokens.spacingHorizontalXXXL },
  },
  // Realus Fluent <Button appearance="subtle" shape="circular"> jau
  // suteikia aukštį/kraštus pagal Fluent2 standartą (žr. #5) — čia tik
  // permatomas baltas atspalvis ant tamsaus fono ir plaukiojanti pozicija.
  iconButton: {
    position: 'absolute',
    backgroundColor: 'rgba(255,255,255,0.1)', color: tokens.colorNeutralForegroundInverted,
    ':hover': { backgroundColor: 'rgba(255,255,255,0.2)', color: tokens.colorNeutralForegroundInverted },
  },
  closeBtn: {
    top: tokens.spacingVerticalL, right: tokens.spacingHorizontalL, width: '40px', height: '40px',
    '@media (min-width: 640px)': { top: tokens.spacingVerticalXXL, right: tokens.spacingHorizontalXXL },
  },
  prevBtn: {
    left: tokens.spacingHorizontalS, top: '50%', transform: 'translateY(-50%)', width: '44px', height: '44px',
    '@media (min-width: 640px)': { left: tokens.spacingHorizontalXXL },
  },
  nextBtn: {
    right: tokens.spacingHorizontalS, top: '50%', transform: 'translateY(-50%)', width: '44px', height: '44px',
    '@media (min-width: 640px)': { right: tokens.spacingHorizontalXXL },
  },
  figure: {
    margin: 0, maxWidth: '64rem', width: '100%', maxHeight: '100%',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: tokens.spacingVerticalM,
  },
  whiteBgBox: {
    backgroundColor: '#ffffff', // techninio brėžinio "popieriaus" fonas — visada baltas, nepriklausomai nuo temos
    borderRadius: tokens.borderRadiusLarge,
    padding: tokens.spacingHorizontalL,
    '@media (min-width: 640px)': { padding: tokens.spacingHorizontalXXL },
    maxWidth: '100%', maxHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  imgWhite: { maxWidth: '100%', objectFit: 'contain', maxHeight: 'calc(80vh - 3rem)' },
  imgPlain: { maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: tokens.borderRadiusLarge },
  caption: { color: 'rgba(255,255,255,0.8)', fontSize: tokens.fontSizeBase300, textAlign: 'center' },
  counter: {
    position: 'absolute', bottom: tokens.spacingVerticalL, left: '50%', transform: 'translateX(-50%)',
    color: 'rgba(255,255,255,0.7)', fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightMedium,
    '@media (min-width: 640px)': { bottom: tokens.spacingVerticalXXL },
  },
});

// Bendras, pilno ekrano paveikslėlių peržiūros dialogas — naudojamas ir
// "Įgyvendinti panašių baldų projektai" galerijai, ir baldo brėžinių (3D/techninio)
// didesniam peržiūrėjimui. `items` — [{ src, alt }]; `index` — kuris rodomas dabar.
//
// Lieka paprastu `fixed` overlay (NE Fluent <Dialog>) — Dialog'o standartinė
// centruota/ribota surface'o chrome (fonas, paddingai, uždarymo animacija)
// susikirstų su čia reikalingu pilno ekrano juodu fonu ir klaviatūros
// kairė/dešinė navigacija, o realios naudos (focus trap ir pan.) šiam
// atskiram, jau savo ESC/klaviatūros logiką turinčiam komponentui nebūtų.
const ImageLightbox = ({ items, index, onClose, onNavigate }) => {
  const s = useStyles();
  const { t } = useTranslation();
  const canNav = items.length > 1;

  const goPrev = useCallback(() => {
    if (canNav) onNavigate((index - 1 + items.length) % items.length);
  }, [canNav, index, items.length, onNavigate]);

  const goNext = useCallback(() => {
    if (canNav) onNavigate((index + 1) % items.length);
  }, [canNav, index, items.length, onNavigate]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, goPrev, goNext]);

  const item = items[index];
  if (!item) return null;

  return (
    <div
      className={s.overlay}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <Button
        appearance="subtle"
        shape="circular"
        onClick={onClose}
        aria-label={t('shop.image_lightbox.close')}
        icon={<CloseIcon style={{ fontSize: 22 }} />}
        className={mergeClasses(s.iconButton, s.closeBtn)}
      />

      {canNav && (
        <Button
          appearance="subtle"
          shape="circular"
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          aria-label={t('shop.image_lightbox.prev')}
          icon={<ChevronLeftIcon style={{ fontSize: 24 }} />}
          className={mergeClasses(s.iconButton, s.prevBtn)}
        />
      )}

      <figure
        className={s.figure}
        onClick={(e) => e.stopPropagation()}
      >
        {item.whiteBg ? (
          <div className={s.whiteBgBox}>
            <img src={item.src} alt={item.alt || ''} className={s.imgWhite} />
          </div>
        ) : (
          <img src={item.src} alt={item.alt || ''} className={s.imgPlain} />
        )}
        {item.alt && <figcaption className={s.caption}>{item.alt}</figcaption>}
      </figure>

      {canNav && (
        <Button
          appearance="subtle"
          shape="circular"
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          aria-label={t('shop.image_lightbox.next')}
          icon={<ChevronRightIcon style={{ fontSize: 24 }} />}
          className={mergeClasses(s.iconButton, s.nextBtn)}
        />
      )}

      {canNav && (
        <div className={s.counter}>
          {index + 1} / {items.length}
        </div>
      )}
    </div>
  );
};

export default ImageLightbox;
